import { ItemBroadcastStatus, ItemStatusBroadcast } from "../../../src/taskpane/interfaces/signalR/ItemStatusBroadcast";
import type { IExternalResourceUrl } from "../ContentBodyFilesFolders";

/**
 * Pure routing for ItemHub (SignalR) broadcasts in the cross-library "All documents"
 * grid.
 *
 * The single-library grid can IGNORE `broadcast.listId`: its connection joins exactly
 * one hub group, so everything it receives belongs to the list on screen. The aggregate
 * grid joins one group PER library on a SHARED connection, which makes the list id the
 * only thing that says which library's rows an update belongs to — routing without it
 * would apply library A's item 42 to library B's item 42.
 *
 * The matched id handed to the handlers is the ENLISTED spelling, not the broadcast's:
 * the hook resolves cursors from the library's own list id (one list may back several
 * folder-scoped cursors), so a differently-cased id on the wire must not turn into a
 * lookup miss.
 */

export interface IItemBroadcastHandlers {
    /** A new item appeared in the library (automation created it). */
    onCreated: (listId: string, itemId: string) => void;
    onDeleted: (listId: string, itemId: string) => void;
    /** Automation took the item — the row greys out until the next update. */
    onLocked: (listId: string, itemId: string) => void;
    /** Updated/Unlocked (and any future status): re-read the row. */
    onUpdated: (listId: string, itemId: string) => void;
    onExternalResource: (resource: IExternalResourceUrl) => void;
}

export interface IItemBroadcastContext {
    /** List ids this view enlisted for; matched case-insensitively. */
    enlistedListIds: string[];
    /** Signed-in account — ExternalResource is user-targeted and drops on a mismatch. */
    currentAccountId?: string;
    /** Panel title fallback when the payload carries no description and no host. */
    externalContentLabel?: string;
}

/**
 * Wire format is `"<url>"` or `"<url>, <description>"`. Anything past the first
 * separator is description (a description may itself contain ", "), and an empty one
 * falls back to the url's host, then to the caller's label.
 */
export const parseExternalResourceUrl = (value: string, fallbackDescription?: string): IExternalResourceUrl | null => {
    const parts = String(value ?? "").split(", ");
    const url = parts[0]?.trim();
    if (!url) {
        return null;
    }

    const described = parts.length > 1 ? parts.slice(1).join(", ").trim() : "";
    let host = "";
    try {
        host = new URL(url).hostname;
    } catch {
        host = "";
    }

    return { url, description: described || host || fallbackDescription };
};

/**
 * Dispatches one broadcast to the matching handler. Returns whether it was acted on —
 * broadcasts for lists this view never enlisted, or without a real item id, are dropped.
 */
export const routeItemBroadcast = (
    broadcast: ItemStatusBroadcast,
    context: IItemBroadcastContext,
    handlers: IItemBroadcastHandlers
): boolean => {
    if (!(broadcast?.itemId > 0)) {
        return false;
    }

    const broadcastListId = String(broadcast.listId ?? "").toLowerCase();
    const listId = (context?.enlistedListIds ?? [])
        .find(candidate => candidate && String(candidate).toLowerCase() === broadcastListId);
    if (!listId) {
        return false;
    }

    const itemId = String(broadcast.itemId);

    switch (broadcast.status) {
        case ItemBroadcastStatus.Created:
            handlers.onCreated(listId, itemId);
            return true;
        case ItemBroadcastStatus.Deleted:
            handlers.onDeleted(listId, itemId);
            return true;
        case ItemBroadcastStatus.Locked:
            handlers.onLocked(listId, itemId);
            return true;
        case ItemBroadcastStatus.ExternalResource: {
            // Broadcast to the whole list group, but meant for the ONE user whose
            // automation produced it — everyone else must not get a popup panel.
            const target = broadcast.accountId?.toLowerCase();
            const current = context?.currentAccountId?.toLowerCase();
            if (!target || !current || target !== current) {
                return false;
            }
            const resource = parseExternalResourceUrl(broadcast.externalResourceUrl, context?.externalContentLabel);
            if (!resource) {
                return false;
            }
            handlers.onExternalResource(resource);
            return true;
        }
        default:
            // Updated, Unlocked, and anything a newer server adds: re-read the row.
            // Deliberately the DEFAULT (not a case list) so an unknown status degrades
            // to a refresh instead of being silently swallowed.
            handlers.onUpdated(listId, itemId);
            return true;
    }
};
